import type { EhsrClause } from '../../ehsr/types'

export type EhsrHierarchyNode = {
  nodeId: string
  kind: 'group' | 'leaf'
  title: string
  clause?: EhsrClause
  children: EhsrHierarchyNode[]
}

function getPrefixPath(clauseId: string): string[] {
  const parts = clauseId.split('.')
  const prefixes: string[] = []
  for (let index = 1; index <= parts.length; index += 1) {
    prefixes.push(parts.slice(0, index).join('.'))
  }
  return prefixes
}

function getTitleForNodeId(clause: EhsrClause, nodeId: string): string | undefined {
  const fromSectionLabels = clause.sectionLabels?.[nodeId]
  if (fromSectionLabels != null && fromSectionLabels.length > 0) {
    return fromSectionLabels
  }
  return undefined
}

type MutableNode = {
  nodeId: string
  kind: 'group' | 'leaf'
  title: string
  clause?: EhsrClause
  children: Map<string, MutableNode>
}

function compareNodeIds(left: string, right: string): number {
  const leftParts = left.split('.').map((part) => Number(part))
  const rightParts = right.split('.').map((part) => Number(part))
  const maxLength = Math.max(leftParts.length, rightParts.length)

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = leftParts[index] ?? 0
    const rightValue = rightParts[index] ?? 0
    if (leftValue !== rightValue) {
      return leftValue - rightValue
    }
  }

  return 0
}

function toImmutableNode(node: MutableNode): EhsrHierarchyNode {
  const children = [...node.children.values()]
    .sort((left, right) => compareNodeIds(left.nodeId, right.nodeId))
    .map(toImmutableNode)

  return {
    nodeId: node.nodeId,
    kind: node.kind,
    title: node.title,
    clause: node.clause,
    children,
  }
}

/** Build nested hierarchy from clause ids using dot-prefix grouping. */
export function buildEhsrHierarchyTree(clauses: EhsrClause[]): EhsrHierarchyNode[] {
  const root = new Map<string, MutableNode>()

  const sortedClauses = [...clauses].sort((left, right) =>
    compareNodeIds(left.id, right.id)
  )

  for (const clause of sortedClauses) {
    const prefixPath = getPrefixPath(clause.id)

    let currentLevel = root
    for (let depth = 1; depth <= prefixPath.length; depth += 1) {
      const nodeId = prefixPath[depth - 1]!
      const isLeaf = depth === prefixPath.length
      const title = getTitleForNodeId(clause, nodeId) ?? nodeId

      let node = currentLevel.get(nodeId)
      if (!node) {
        node = {
          nodeId,
          kind: isLeaf ? 'leaf' : 'group',
          title,
          clause: isLeaf ? clause : undefined,
          children: new Map<string, MutableNode>(),
        }
        currentLevel.set(nodeId, node)
      } else if (!isLeaf && node.title === nodeId && title !== nodeId) {
        node.title = title
      } else if (isLeaf) {
        node.kind = 'leaf'
        node.clause = clause
        node.title = title
      }

      currentLevel = node.children
    }
  }

  return [...root.values()]
    .sort((left, right) => compareNodeIds(left.nodeId, right.nodeId))
    .map(toImmutableNode)
}
